import React, { useState, useEffect } from 'react';
import './Steps.css';
import { createBooking, initiatePayment } from '../../services/api';

const Step6 = ({ bookingData, updateBookingData, prevStep, handleConfirmBooking }) => {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isPublicGroup = bookingData.lessonType === 'group';

  useEffect(() => {
    setPaymentMethod('cash');
    updateBookingData('paymentMethod', 'cash');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculateTotalPrice();
    // eslint-disable-next-line
  }, [bookingData.packageType, bookingData.packageQuantity, bookingData.weeksCount, bookingData.numberOfParticipants, bookingData.lessonType]);

  const calculateTotalPrice = () => {
    // If it's a public group, don't calculate - price is variable
    if (isPublicGroup) {
      setTotalPrice(0);
      return;
    }

    let pricePerPerson = 0;
    let multiplier = 1;

    // Get hourly price based on participants
    const getHourlyPrice = (participants) => {
      if (participants === 1) return 170;
      if (participants === 2) return 150;
      if (participants === 3) return 125;
      if (participants >= 4) return 105;
      return 170;
    };

    // Get Ski Day price
    const getSkiDayPrice = (participants) => {
      if (participants === 1) return 700;
      if (participants >= 2) return 500;
      return 700;
    };

    switch (bookingData.packageType) {
      case 'single':
        pricePerPerson = getHourlyPrice(bookingData.numberOfParticipants);
        multiplier = 1;
        break;
      case 'multiple':
        pricePerPerson = getHourlyPrice(bookingData.numberOfParticipants);
        multiplier = bookingData.packageQuantity;
        break;
      case 'skiday':
        pricePerPerson = getSkiDayPrice(bookingData.numberOfParticipants);
        multiplier = bookingData.packageQuantity || 1;
        break;
      case 'weekly':
        pricePerPerson = 500;
        multiplier = bookingData.weeksCount;
        break;
      case 'seasonal':
        pricePerPerson = 1800;
        multiplier = 1;
        break;
      default:
        pricePerPerson = 0;
    }

    const total = pricePerPerson * multiplier * bookingData.numberOfParticipants;
    setTotalPrice(total);
  };

  const getPackageTitle = () => {
    switch (bookingData.packageType) {
      case 'single':
        return 'Lecție Unică';
      case 'multiple':
        return `${bookingData.packageQuantity} Lecții Multiple`;
      case 'skiday':
        return `Pachet Ski Day (${bookingData.packageQuantity || 1} ${(bookingData.packageQuantity || 1) === 1 ? 'zi' : 'zile'})`;
      case 'weekly':
        return `Pachet Săptămânal (${bookingData.weeksCount} ${bookingData.weeksCount === 1 ? 'săptămână' : 'săptămâni'})`;
      case 'seasonal':
        return 'Pachet Sezon Complet';
      default:
        return '';
    }
  };

  const renderSchedule = () => {
    const isRecurring = bookingData.packageType === 'weekly' || bookingData.packageType === 'seasonal';
    const selectedDays = bookingData.daysPerWeek || [];

    if (isRecurring) {
      return (
        <>
          <div className="summary-item">
            <span className="summary-label">Zile selectate:</span>
            <span className="summary-value">{selectedDays.join(', ')}</span>
          </div>
          {selectedDays.map(day => (
            <div key={day} className="summary-item schedule-detail">
              <span className="summary-label">{day}:</span>
              <span className="summary-value">{bookingData.timeSlots[day]}</span>
            </div>
          ))}
          {bookingData.packageType === 'weekly' && (
            <div className="summary-item schedule-note">
              <span className="summary-label">Periodicitate:</span>
              <span className="summary-value">În fiecare săptămână, {bookingData.weeksCount} {bookingData.weeksCount === 1 ? 'săptămână' : 'săptămâni'}</span>
            </div>
          )}
          {bookingData.packageType === 'seasonal' && (
            <div className="summary-item schedule-note">
              <span className="summary-label">Periodicitate:</span>
              <span className="summary-value">În fiecare săptămână, tot sezonul</span>
            </div>
          )}
        </>
      );
    } else {
      return (
        <>
          <div className="summary-item">
            <span className="summary-label">Zile selectate:</span>
            <span className="summary-value">{selectedDays.join(', ')}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Oră:</span>
            <span className="summary-value">{bookingData.timeSlots['all']}</span>
          </div>
          {bookingData.packageType === 'multiple' && (
            <div className="summary-item schedule-note">
              <span className="summary-label">Total lecții:</span>
              <span className="summary-value">{bookingData.packageQuantity}</span>
            </div>
          )}
        </>
      );
    }
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    updateBookingData('paymentMethod', method);
  };

  const formatScheduleForAPI = (bookingData) => {
    const schedule = [];
    
    if (bookingData.packageType === 'weekly' || bookingData.packageType === 'seasonal') {
      bookingData.daysPerWeek?.forEach(day => {
        const time = bookingData.timeSlots[day];
        if (time) {
          schedule.push({
            date: String(day),
            time: String(time)
          });
        }
      });
    } else {
      bookingData.selectedDates?.forEach(date => {
        const dateObj = new Date(date);
        const dateKey = dateObj.toISOString().split('T')[0];
        const time = bookingData.timeSlots[dateKey];
        
        if (time) {
          schedule.push({
            date: dateKey,
            time: String(time)
          });
        }
      });
    }
    
    return schedule;
  };

  const handleConfirm = async () => {
    if (!agreedToTerms) return;
    
    setLoading(true);
    setError(null);

    try {
      const apiBookingData = {
        numberOfParticipants: bookingData.numberOfParticipants,
        packageType: bookingData.packageType,
        packageQuantity: bookingData.packageQuantity,
        weeksCount: bookingData.weeksCount,
        schedule: formatScheduleForAPI(bookingData),
        participants: bookingData.participants.map(p => ({
          name: String(p.name || ''),
          age: parseInt(p.age) || 0,
          level: String(p.level || p.experienceLevel || 'beginner'),
          hasEquipment: Boolean(p.hasEquipment)
        })),
        contactEmail: bookingData.contactEmail,
        contactPhone: bookingData.contactPhone,
        totalPrice: totalPrice,
        paymentMethod: paymentMethod,
        lessonType: bookingData.lessonType,
        instructorId: bookingData.instructorId,
        instructorName: bookingData.instructorName,
        sport: bookingData.sport
      };

      const bookingResponse = await createBooking(apiBookingData);

      updateBookingData('bookingId', bookingResponse.data.booking.bookingId);

      if (paymentMethod === 'online') {
        const paymentResponse = await initiatePayment(bookingResponse.data.booking.bookingId);
        
        // Backend returnează HTML, deschide într-o fereastră nouă
        const paymentWindow = window.open('', '_blank');
        if (paymentWindow) {
          paymentWindow.document.write(paymentResponse.data);
          paymentWindow.document.close();
        } else {
          setError('Vă rugăm să permiteți pop-up-uri pentru această pagină.');
          return;
        }
        
        // Refresh tab actual
        window.location.href = '/';
        return;
      }
      

      handleConfirmBooking();

    } catch (err) {
      console.error('❌ Error:', err);
      setError(err.message || 'A apărut o eroare. Vă rugăm să încercați din nou.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="step-container">
      <h2 className="step-heading">Rezumat & Confirmare</h2>
      <p className="step-description">Verificați detaliile rezervării și alegeți metoda de plată</p>

      <div className="summary-container">
        <div className="summary-section">
          <h3 className="summary-title">📋 Detalii Rezervare</h3>
          <div className="summary-item">
            <span className="summary-label">Număr participanți:</span>
            <span className="summary-value">{bookingData.numberOfParticipants} {bookingData.numberOfParticipants === 1 ? 'persoană' : 'persoane'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Tip lecție:</span>
            <span className="summary-value">{bookingData.lessonType === 'individual' ? 'Individual (Privat)' : 'Grup (Public)'}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Instructor:</span>
            <span className="summary-value">{bookingData.instructorName}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Pachet selectat:</span>
            <span className="summary-value">{getPackageTitle()}</span>
          </div>
        </div>

        <div className="summary-section">
          <h3 className="summary-title">📅 Program</h3>
          {renderSchedule()}
        </div>

        <div className="summary-section">
          <h3 className="summary-title">👥 Participanți</h3>
          {bookingData.participants?.map((p, index) => (
            <div key={index} className="summary-item participant-item">
              <span className="summary-label">{index + 1}. {p.name}</span>
              <span className="summary-value">{p.age} ani • {
                p.experienceLevel === 'beginner' ? 'Începător' :
                p.experienceLevel === 'intermediate' ? 'Intermediar' : 'Avansat'
              }</span>
            </div>
          ))}
        </div>

        <div className="summary-section">
          <h3 className="summary-title">📞 Contact</h3>
          <div className="summary-item">
            <span className="summary-label">Email:</span>
            <span className="summary-value">{bookingData.contactEmail}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Telefon:</span>
            <span className="summary-value">{bookingData.contactPhone}</span>
          </div>
        </div>

        {isPublicGroup ? (
          <div className="total-price-section" style={{background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'}}>
            <div className="total-price-row" style={{flexDirection: 'column', alignItems: 'flex-start', gap: '12px'}}>
              <span className="total-label" style={{color: '#92400e'}}>Preț variabil - Lecție în grup public</span>
              <p style={{fontSize: '14px', color: '#92400e', margin: 0, lineHeight: '1.5'}}>
                📞 Prețul final va fi confirmat telefonic în funcție de numărul total de participanți care se înscriu. Plata se face exclusiv cash la fața locului.
              </p>
            </div>
          </div>
        ) : (
          <div className="total-price-section">
            <div className="total-price-row">
              <span className="total-label">Total de plată:</span>
              <span className="total-amount">{totalPrice} RON</span>
            </div>
          </div>
        )}
      </div>

      <div className="payment-section">
        <h3 className="section-title">💳 Metodă de Plată</h3>
        <div className="payment-methods">
          <label className={`payment-option ${paymentMethod === 'online' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="payment"
              value="online"
              checked={paymentMethod === 'online'}
              onChange={() => handlePaymentMethodChange('online')}
            />
            <div className="payment-content">
              <span className="payment-icon">💳</span>
              <div className="payment-text">
                <div className="payment-title">Plată Online (Card)</div>
                <div className="payment-subtitle">
                  Plată securizată prin Netopia Mobilpay
                </div>
              </div>
            </div>
          </label>

          <label className={`payment-option ${paymentMethod === 'cash' ? 'selected' : ''}`}>
            <input
              type="radio"
              name="payment"
              value="cash"
              checked={paymentMethod === 'cash'}
              onChange={() => handlePaymentMethodChange('cash')}
            />
            <div className="payment-content">
              <span className="payment-icon">💵</span>
              <div className="payment-text">
                <div className="payment-title">Plată Cash</div>
                <div className="payment-subtitle">
                  {isPublicGroup ? 'Obligatoriu pentru lecții în grup public' : 'La locație, înainte de lecție'}
                </div>
              </div>
            </div>
          </label>
        </div>
      </div>
      <div className="terms-section">
  <label className="terms-checkbox">
    <input
      type="checkbox"
      checked={agreedToTerms}
      onChange={(e) => setAgreedToTerms(e.target.checked)}
    />
    <span>Sunt de acord cu{' '}
      <a href="/termeni" target="_blank" rel="noopener noreferrer" className="terms-link">
        termenii și condițiile
      </a>
      {' '}și{' '}
      <a href="/confidentialitate" target="_blank" rel="noopener noreferrer" className="terms-link">
        politica de confidențialitate
      </a>
    </span>
  </label>
</div>

      {error && (
        <div className="error-message">
          <strong>⚠️ Eroare:</strong> {error}
        </div>
      )}

      <div className="step-actions">
        <button className="btn-secondary" onClick={prevStep} disabled={loading}>
          ← Înapoi
        </button>
        <button 
          className="btn-primary btn-confirm"
          onClick={handleConfirm}
          disabled={!agreedToTerms || loading}
        >
          {loading ? '⏳ Se procesează...' : '✓ Confirmă Rezervarea'}
        </button>
      </div>
    </div>
  );
};

export default Step6;